// catalog.js — thin layer over animals.json
//
// Provides typed accessors for resolving an item's asset paths by
// convention:
//
//     /images/{animals|vehicles}/{id}.webp
//     /sounds/animals/{id}.mp3
//     /sounds/intros/{locale}/{id}.mp3
//
// The data file (animals.json) intentionally does NOT hard-code paths —
// this module is the single source of truth for URL construction, so
// renames and reorganizations only have to happen in one place.

import animalsData from './animals.json';

/**
 * @typedef {Object} CatalogItem
 * @property {string} id           - Stable identifier (e.g. "cow")
 * @property {string} labelKey     - i18n key for the display name
 * @property {string} categoryId   - Parent category id
 * @property {string} imagePath    - Absolute URL path to the button image
 * @property {string} soundPath    - Absolute URL path to the sound effect
 * @property {(locale: string) => string} introPath - Intro audio path for a locale
 */

/**
 * @typedef {Object} CatalogCategory
 * @property {string} id
 * @property {string} labelKey
 * @property {string} background
 * @property {CatalogItem[]} items
 */

const CATEGORY_IMAGE_FOLDER = {
  barnyard: 'animals',
  wild: 'animals',
  vehicles: 'vehicles',
};

function buildItem(rawItem, categoryId) {
  const imageFolder = CATEGORY_IMAGE_FOLDER[categoryId] ?? 'animals';
  return {
    id: rawItem.id,
    labelKey: rawItem.labelKey,
    categoryId,
    imagePath: `/images/${imageFolder}/${rawItem.id}.webp`,
    soundPath: `/sounds/animals/${rawItem.id}.mp3`,
    introPath: (locale) => `/sounds/intros/${locale}/${rawItem.id}.mp3`,
  };
}

/** @type {CatalogCategory[]} */
const categories = animalsData.categories.map((cat) => ({
  id: cat.id,
  labelKey: cat.labelKey,
  background: cat.background,
  items: cat.items.map((item) => buildItem(item, cat.id)),
}));

/** @type {Map<string, CatalogItem>} */
const itemsById = new Map();
for (const cat of categories) {
  for (const item of cat.items) {
    if (itemsById.has(item.id)) {
      throw new Error(`[catalog] Duplicate item id: ${item.id}`);
    }
    itemsById.set(item.id, item);
  }
}

/** Return all categories in their declared order. */
export function getCategories() {
  return categories;
}

/** Return a flat array of every catalog item across all categories. */
export function getAllItems() {
  return categories.flatMap((c) => c.items);
}

/** Look up a single item by id. Returns null when not found. */
export function getItemById(id) {
  return itemsById.get(id) ?? null;
}

/** Return the list of supported locale codes. */
export function getSupportedLocales() {
  return [...animalsData.supportedLocales];
}

/** Return the default locale code. */
export function getDefaultLocale() {
  return animalsData.defaultLocale;
}

/** Number of items in the catalog (used by tests and debugging). */
export function getItemCount() {
  return itemsById.size;
}
